import { useToast } from "@chakra-ui/react"
import { useQueryClient } from "@tanstack/react-query"
import { VAPID_PUBLIC_KEY } from "Constants/vapid"
import { useEffect, useState } from "react"
import {
  useCreatePanelistNotificationSubscription,
  useDeletePanelistNotificationSubscription,
  useListNotificationSubscriptions,
} from "~/api/generated/usabilityhub-components"

const SERVICE_WORKER_PATH = "/service-worker.js"

const ARE_SERVICE_WORKERS_SUPPORTED = "serviceWorker" in navigator
const ARE_NOTIFICATIONS_SUPPORTED =
  ARE_SERVICE_WORKERS_SUPPORTED &&
  "showNotification" in ServiceWorkerRegistration.prototype

const DEFAULT_LYSSNA_ICON = "/usercrowd/usercrowd-android-chrome-192x192.png"

function serializeSubscription(subscription: PushSubscription) {
  const { keys } = subscription.toJSON()
  return {
    endpoint: subscription.endpoint,
    public_key: keys!.p256dh,
    auth_secret: keys!.auth,
  }
}

async function installServiceWorker() {
  if (!ARE_SERVICE_WORKERS_SUPPORTED) {
    throw new Error("Browser does not support service workers")
  }
  await navigator.serviceWorker.register(SERVICE_WORKER_PATH)
  return navigator.serviceWorker.ready
}

export const usePushNotifications = () => {
  const toast = useToast()
  const queryClient = useQueryClient()
  const invalidateSubscriptionsQuery = () => {
    queryClient.invalidateQueries(
      ["api", "usercrowd", "notification_subscriptions"],
      { exact: true }
    )
  }

  const [isInitialized, setIsInitialized] = useState(false)
  const [isBusy, setIsBusy] = useState(false)
  const [currentEndpoint, setCurrentEndpoint] = useState<string | null>(null)

  const isSupported = ARE_NOTIFICATIONS_SUPPORTED

  const { data: subscriptions } = useListNotificationSubscriptions({})

  const { mutate: createPanelistNotificationSubscription } =
    useCreatePanelistNotificationSubscription({
      onSuccess: (data) => {
        setCurrentEndpoint(data.endpoint)
        invalidateSubscriptionsQuery()

        // NOTE: We want to use a browser notification not a toast here to demo
        // the browser notification to the user
        showNotification("Notifications enabled", {
          body: "You will now see a notification like this when a test is available!",
        })
      },
      onError: (e) => {
        // We don't want to show any errors before the hook is initialized
        // because we might call this code on page load
        if (suppressErrors) {
          setSuppressErrors(false)
          return
        }

        if (e.status === 400) {
          toast({
            status: "error",
            title: "Unable to turn on notifications",
          })
        } else {
          toast({
            status: "error",
            title: "Sorry, something went wrong. Try reloading the page.",
          })
        }
      },
    })

  const { mutate: deletePanelistNotificationSubscription } =
    useDeletePanelistNotificationSubscription({
      onSuccess: async () => {
        invalidateSubscriptionsQuery()

        toast({
          status: "success",
          title:
            "You will no longer receive notifications when tests are available",
        })
      },
      onError: (e) => {
        if (e.status === 404) {
          // Don't show an error if the subscription doesn't exist
        } else {
          toast({
            status: "error",
            title: "Sorry, something went wrong. Try reloading the page.",
          })
        }
      },
    })

  const pushManagerSubscribe = async (): Promise<PushSubscription | null> => {
    try {
      const registration = await installServiceWorker()
      return await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: VAPID_PUBLIC_KEY,
      })
    } catch (error) {
      // Catch both `DOMException` `NotAllowedError` and the `AbortError`
      // equivalent.
      //
      // The difference in case that might raise either one is unclear to me at
      // the time of writing, but this `catch` handler is exhaustive.
      // Previously we were relying on `Notification.permission === "denied"`
      // but this apparently was not enough (see #3172).
      if (
        error.name === "DOMException" ||
        error.name === "NotAllowedError" ||
        error.name === "AbortError" ||
        Notification.permission === "denied"
      ) {
        return null
      }

      throw error
    }
  }

  const subscribe = async (): Promise<void> => {
    const pushSubscription = await pushManagerSubscribe()
    if (pushSubscription) {
      const subscription = serializeSubscription(pushSubscription)
      createPanelistNotificationSubscription({
        body: {
          panelist_notification_subscription: subscription,
        },
      })
    } else {
      toast({
        status: "error",
        title:
          "Could not enable notifications because permission was not granted",
      })
    }
  }

  // Unsubscribe the push subscription associated with this browser.
  const unsubscribe = async () => {
    if (!subscriptions) return

    const pushSubscription = await getPushSubscription()
    if (pushSubscription == null) return null

    const isSuccessful = await pushSubscription.unsubscribe()
    if (!isSuccessful) {
      // Just throw if this is false. By my reading of MDN this is won't
      // actually happen because the promise will be rejected on failure.
      //
      // See: https://developer.mozilla.org/en-US/docs/Web/API/PushSubscription/unsubscribe
      throw new Error("Unsubscription was unsuccessful")
    }

    const getSubscriptionByEndpoint = (endpoint: string) => {
      return (
        subscriptions.notification_subscriptions.find(
          (s) => s.endpoint === endpoint
        ) ?? null
      )
    }

    const subscription = getSubscriptionByEndpoint(pushSubscription.endpoint)
    if (subscription == null) return null

    // Delete the subscription
    deletePanelistNotificationSubscription({
      pathParams: { id: subscription.id },
    })
    return subscription
  }

  const [suppressErrors, setSuppressErrors] = useState(false)
  useEffect(() => {
    if (isInitialized || !isSupported || !subscriptions) return

    setIsInitialized(true)

    const init = async () => {
      const pushSubscription = await getPushSubscription()

      if (!pushSubscription) return

      setCurrentEndpoint(pushSubscription.endpoint)

      const activeSubscription = subscriptions.notification_subscriptions.find(
        (s) => s.endpoint === pushSubscription.endpoint
      )

      // The browser thinks it is subscribed but we had no record of it.
      if (!activeSubscription) {
        // Try silently enabling notification since there may have been an error
        // posting the original subscription info to our servers
        //
        // If there was an error it’s probably because another user has already
        // subscribed on this browser OR a race condition which we handle as a
        // 400 error, we want to ignore this since we can only have one
        // registered push notification per browser.
        setSuppressErrors(true)
        await subscribe()
      }
    }
    init()
  }, [isInitialized, subscriptions])

  const activeSubscription =
    currentEndpoint &&
    subscriptions?.notification_subscriptions.find(
      (s) => s.endpoint === currentEndpoint
    )

  const setEnabled = async (enabled: boolean) => {
    if (!isSupported) return
    setIsBusy(true)

    if (enabled) {
      await subscribe()
    } else {
      await unsubscribe()
    }
    setIsBusy(false)
  }

  return {
    isSupported,
    isInitialized,
    isEnabled: !!activeSubscription,
    isBusy,
    setEnabled,
  }
}

// `navigator.serviceWorker.ready` will never resolve if there is no service
// worker already installed, so we need to check first if there are any
// existing registrations.
const checkRegistered = async () => {
  // NOTE: `getRegistrations` is not supported on some devices (Android Chrome)
  // at the time of writing.
  return (await navigator.serviceWorker.getRegistration()) !== undefined
}

const getPushSubscription = async () => {
  const isRegistered = await checkRegistered()
  if (!isRegistered) return null

  // Although the `getRegistrations` API suggests that there may be multiple
  // registrations, I believe that it is actually not possible to have more
  // than one.
  const registration = await navigator.serviceWorker.ready
  return registration.pushManager.getSubscription()
}

const showNotification = async (
  title: string,
  options: NotificationOptions = {}
) => {
  const registration = await navigator.serviceWorker.ready
  return registration.showNotification(title, {
    icon: DEFAULT_LYSSNA_ICON,
    ...options,
  })
}
